import { useState } from "react";
import { Users, ArrowDownUp } from "lucide-react";
import { compareMeta } from "@/data/compare";
import { fxData } from "@/data/fx";

export const BudgetEstimator = ({ slug }) => {
  const meta = compareMeta[slug];
  const fx = fxData[slug];
  const [size, setSize] = useState(4);
  const [usd, setUsd] = useState("1000");
  if (!meta) return null;

  const mult = Math.pow(size / 4, 0.8);
  const low = Math.round((meta.costLow * mult) / 50) * 50;
  const high = Math.round((meta.costHigh * mult) / 50) * 50;

  const usdNum = Math.max(0, parseFloat(usd.replace(/,/g, "")) || 0);
  const local = fx ? usdNum * fx.perUsd : 0;
  const fmtLocal = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: n < 100 ? 2 : 0 }).format(n);

  return (
    <div className="mb-8 bg-pine-ink text-cream rounded-2xl p-6 lg:p-8 grain relative" data-testid="budget-estimator">
      <div className="flex flex-col lg:flex-row lg:items-center gap-8">
        <div className="flex-1">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft flex items-center gap-2">
            <Users size={13} /> Budget estimator
          </p>
          <h3 className="mt-3 font-serif font-semibold text-2xl">How big is your caravan?</h3>
          <p className="mt-2 text-sm text-sage-light leading-relaxed">
            Slide to your family size — the estimate scales rent, food, and schooling from the family-of-four baseline.
          </p>
          <div className="mt-6">
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              data-testid="budget-slider"
              aria-label="Family size"
              className="w-full accent-[#D9777F] cursor-pointer"
            />
            <div className="flex justify-between font-mono text-[10px] text-sage-light mt-1">
              <span>SOLO</span>
              <span>FAMILY OF 4</span>
              <span>10+</span>
            </div>
          </div>
        </div>
        <div className="lg:w-72 bg-pine-card border border-cream/10 rounded-2xl p-6 text-center shrink-0">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sage-light">Family of</p>
          <p className="font-serif font-bold text-5xl text-petal-soft my-2" data-testid="budget-family-size">{size}</p>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sage-light mt-4">Estimated monthly</p>
          <p className="font-serif font-semibold text-2xl lg:text-3xl text-cream mt-1" data-testid="budget-estimate">
            ${low.toLocaleString()} – ${high.toLocaleString()}
          </p>
          <p className="text-[11px] text-sage-light mt-3">Modest-comfortable living, USD. Your barakah may vary.</p>
        </div>
      </div>

      {fx && (
        <div className="mt-7 pt-6 border-t border-cream/10" data-testid="currency-converter">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-petal-soft flex items-center gap-2 mb-4">
            <ArrowDownUp size={13} /> Pocket money converter
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-2 bg-pine-card border border-cream/10 rounded-xl px-4 py-3 flex-1">
              <span className="font-mono text-sm text-sage-light">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={usd}
                onChange={(e) => setUsd(e.target.value.replace(/[^0-9.,]/g, ""))}
                data-testid="converter-usd-input"
                aria-label="Amount in US dollars"
                className="w-full bg-transparent font-mono text-lg text-cream focus:outline-none"
              />
              <span className="font-mono text-[10px] tracking-widest text-sage-light">USD</span>
            </div>
            <div className="bg-petal/15 border border-petal/30 rounded-xl px-4 py-3 flex-1 text-center sm:text-left">
              <span className="font-mono text-lg text-petal-soft" data-testid="converter-result">
                {fmtLocal(local)} {fx.code}
              </span>
            </div>
            <div className="flex gap-2">
              {["500", "2000", "5000"].map((v) => (
                <button
                  key={v}
                  data-testid={`converter-chip-${v}`}
                  onClick={() => setUsd(v)}
                  className="font-mono text-[11px] px-3 py-2 rounded-full border border-cream/15 text-sage-light hover:border-petal hover:text-petal-soft transition-colors"
                >
                  ${Number(v).toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-3 text-[11px] text-sage-light italic">Approximate 2026 rate (1 USD ≈ {fx.rate}) — {fx.note}. Check a live rate before big money moves.</p>
        </div>
      )}
    </div>
  );
};
